import { useMutation } from '@tanstack/react-query';

import { enderecoDeArquivo } from '@/shared/api/client';
import { policies, type IssuanceReportLink, type Policy } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { useToast } from '@/shared/ui/useToast';

import type { AcaoDoMenu } from '../users/MenuDeAcoes';

import { motivoSemDocumento } from './documentoDaApolice';
import { avisoDoModeloDoLaudo, motivoSemLaudo, PERMISSAO_DO_LAUDO } from './laudo';

/**
 * Os downloads do "Baixar arquivos" (PolicyDownloadsMenu) como itens do menu da linha da
 * carteira da corretora (listaDeApolicesDaCorretora.tsx): a apolice, o endosso mais
 * recente, o laudo (com a permissao) e os boletos. O endereco e temporario e abre em aba
 * nova, como no menu da seguradora.
 */
export function useDownloadsDaApolice() {
  const toast = useToast();
  const { can } = useAuth();
  const baixar = useMutation({
    mutationFn: ({
      policy,
      qual,
    }: {
      policy: Policy;
      qual: 'apolice' | 'endosso' | 'laudo';
    }) =>
      qual === 'apolice'
        ? policies.document(policy.id)
        : qual === 'laudo'
          ? policies.issuanceReport(policy.id)
          : policies.lastEndorsementDocument(policy.id),
    onSuccess: (link, { qual }) => {
      window.open(enderecoDeArquivo(link.url), '_blank', 'noopener,noreferrer');
      const aviso =
        qual === 'laudo' ? avisoDoModeloDoLaudo(link as IssuanceReportLink) : null;
      if (aviso) toast.info(aviso);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const itens = (policy: Policy, onBillets: () => void): AcaoDoMenu[] => {
    const lista: AcaoDoMenu[] = [
      {
        rotulo: 'Download da apolice (original)',
        motivo: motivoSemDocumento(policy),
        onSelect: () => baixar.mutate({ policy, qual: 'apolice' }),
      },
    ];
    if ((policy.endorsement_count ?? 0) > 0) {
      lista.push({
        rotulo: 'Download do endosso (mais recente)',
        onSelect: () => baixar.mutate({ policy, qual: 'endosso' }),
      });
    }
    if (can(PERMISSAO_DO_LAUDO)) {
      lista.push({
        rotulo: 'Download do laudo de emissao (original)',
        motivo: motivoSemLaudo(policy),
        onSelect: () => baixar.mutate({ policy, qual: 'laudo' }),
      });
    }
    lista.push({ rotulo: 'Download do(s) boleto(s) (original)', onSelect: onBillets });
    return lista;
  };

  const baixando = (policy: Policy) =>
    baixar.isPending && baixar.variables?.policy.id === policy.id;

  return { itens, baixando };
}
